// frontend/src/pages/OrderTracking.jsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp as faWhatsappBrand } from "@fortawesome/free-brands-svg-icons";
import {
  faSearch,
  faTruck,
  faBox,
  faCheckCircle,
  faClock,
  faMapMarkerAlt,
  faCalendarAlt,
  faShippingFast,
  faExclamationTriangle,
  faShip,
  faAnchor,
  faBoxes,
  faUser,
  faEnvelope,
  faPhone,
  faBoxOpen,
  faSeedling,
  faArrowLeft,
  faSpinner,
  faGlobe,
  faWarehouse,
  faBuilding,
  faRocket,
  faInfoCircle,
  faBell,
  faCheckDouble,
  faLocationDot,
  faCircle,
  faEllipsisH,
  faPackage,
  faDollarSign,
  faClipboardCheck,
  faUserCheck,
  faStore,
  faArrowRight,
  faWhatsapp,
} from "@fortawesome/free-solid-svg-icons";
import {
  trackShipment,
  getShipmentByOrder,
  getUserOrders,
  getAllShipments,
} from "../api";
import Loader from "../components/UI/Loader";
import SEO from "../components/SEO/SEO";
import "../styles/tracking.scss";

// ============================================
// HELPERS
// ============================================
const getStatusColor = (status) => {
  const colors = {
    ordered: "#D9A321",
    processing: "#3b82f6",
    packed: "#8b5cf6",
    shipped: "#8b5cf6",
    in_transit: "#f59e0b",
    customs: "#ef4444",
    local_delivery: "#10b981",
    out_for_delivery: "#f59e0b",
    delivered: "#16a34a",
    delayed: "#dc2626",
    cancelled: "#6b7280",
  };
  return colors[status] || "#5A5A5A";
};

const getStatusIcon = (status) => {
  const icons = {
    ordered: faClock,
    processing: faBox,
    packed: faBoxOpen,
    shipped: faShippingFast,
    in_transit: faTruck,
    customs: faExclamationTriangle,
    local_delivery: faTruck,
    out_for_delivery: faTruck,
    delivered: faCheckCircle,
    delayed: faExclamationTriangle,
    cancelled: faExclamationTriangle,
  };
  return icons[status] || faTruck;
};

const getStatusLabel = (status) => {
  const labels = {
    ordered: "Order Received",
    processing: "Processing",
    packed: "Packed & Ready",
    shipped: "Shipped",
    in_transit: "In Transit",
    customs: "Customs Clearance",
    local_delivery: "Local Delivery",
    out_for_delivery: "Out for Delivery",
    delivered: "Delivered",
    delayed: "Delayed",
    cancelled: "Cancelled",
  };
  return labels[status] || status;
};

const getProgressPercentage = (status) => {
  const steps = {
    ordered: 5,
    processing: 20,
    packed: 35,
    shipped: 50,
    in_transit: 65,
    customs: 75,
    local_delivery: 85,
    out_for_delivery: 95,
    delivered: 100,
    delayed: 50,
    cancelled: 0,
  };
  return steps[status] || 0;
};

// ============================================
// MAIN COMPONENT
// ============================================
const OrderTracking = () => {
  const { user } = useAuth();
  const [trackingNumber, setTrackingNumber] = useState("");
  const [shipment, setShipment] = useState(null);
  const [myShipments, setMyShipments] = useState([]);
  const [inventoryShipments, setInventoryShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("track");
  const [isSearching, setIsSearching] = useState(false);
  const [showInventory, setShowInventory] = useState(false);

  // ============================================
  // FETCH SHIPMENTS
  // ============================================
  useEffect(() => {
    if (user) {
      fetchUserShipments();
      fetchInventoryShipments();
    }
  }, [user]);

  const fetchUserShipments = async () => {
    try {
      const orders = await getUserOrders();
      const shipmentsData = [];

      for (const order of orders.data) {
        try {
          const shipmentRes = await getShipmentByOrder(order._id);
          if (shipmentRes.data) {
            shipmentsData.push({
              ...shipmentRes.data,
              orderNumber: order.orderNumber,
            });
          }
        } catch (err) {}
      }
      setMyShipments(shipmentsData);
    } catch (error) {
      console.error("Error fetching user shipments:", error);
    }
  };

  const fetchInventoryShipments = async () => {
    try {
      const response = await getAllShipments({ limit: 50 });
      const shipments = response.data?.shipments || [];
      const inventory = shipments.filter(
        (s) => s.orderType === "inventory" && s.isActive !== false,
      );
      setInventoryShipments(inventory);
    } catch (error) {
      console.error("Error fetching inventory shipments:", error);
    }
  };

  // ============================================
  // HANDLERS
  // ============================================
  const handleTrack = async (e) => {
    e.preventDefault();
    if (!trackingNumber.trim()) return;

    setIsSearching(true);
    setLoading(true);
    setError("");
    setShipment(null);

    try {
      const { data } = await trackShipment(trackingNumber);
      setShipment(data);
    } catch (err) {
      setError(err.response?.data?.message || "Tracking number not found");
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  const handleViewShipment = (shipmentData) => {
    setShipment(shipmentData);
    setActiveTab("track");
    setTrackingNumber(shipmentData.trackingNumber);
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <>
      <SEO
        title="Track Order - Kenali Enterprise"
        description="Track your Kenali Enterprise order in real-time. Get updates on your livestock feed supplement shipment from global sourcing to delivery."
        keywords="track order, Kenali Enterprise, shipment tracking, livestock feed delivery"
        canonical="https://kenalienterprise.com/track-order"
      />
      <motion.div
        className="tracking-page"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        {/* Hero */}
        <section className="tracking-hero">
          <div className="tracking-hero__bg" />
          <div className="container tracking-hero__content">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <FontAwesomeIcon icon={faTruck} className="tracking-hero__icon" />
              <h1>Track Your Order</h1>
              <p>
                Enter your tracking number to get real-time updates on your
                shipment from Kenali Enterprise. All products are sourced from
                trusted global partners.
              </p>
              <div className="tracking-hero__info">
                <span>
                  <FontAwesomeIcon icon={faGlobe} /> Global Sourcing
                </span>
                <span>
                  <FontAwesomeIcon icon={faBuilding} /> Trusted Partners
                </span>
                <span>
                  <FontAwesomeIcon icon={faShip} /> Premium Quality
                </span>
              </div>
              <a
                href="https://wa.me/23231030808"
                target="_blank"
                rel="noopener noreferrer"
                className="tracking-hero__whatsapp"
              >
                <FontAwesomeIcon icon={faWhatsappBrand} /> Need help? WhatsApp
                us
              </a>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <div className="container tracking-content">
          <button className="tracking-back" onClick={() => window.history.back()}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </button>

          {/* Inventory Notice */}
          {inventoryShipments.length > 0 && (
            <motion.div
              className="tracking-inventory-notice"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="tracking-inventory-notice__content">
                <FontAwesomeIcon icon={faShip} />
                <div>
                  <strong>New Shipment from Trusted Partners!</strong>
                  <span>
                    {inventoryShipments.length} shipment
                    {inventoryShipments.length > 1 ? "s" : ""} currently in
                    transit. Pre-order customers will be notified when cleared.
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowInventory(!showInventory)}
                className="tracking-inventory-notice__toggle"
              >
                {showInventory ? (
                  <>
                    <FontAwesomeIcon icon={faArrowRight} /> Hide Details
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faBox} /> View Details
                  </>
                )}
              </button>
            </motion.div>
          )}

          {/* Inventory Shipments List */}
          <AnimatePresence>
            {showInventory && inventoryShipments.length > 0 && (
              <motion.div
                className="tracking-inventory-list"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
              >
                <h4>
                  <FontAwesomeIcon icon={faShip} /> Incoming Shipments
                </h4>
                {inventoryShipments.map((ship) => (
                  <div key={ship._id} className="tracking-inventory-item">
                    <div>
                      <div className="tracking-inventory-item__tracking">
                        {ship.trackingNumber}
                        <span className="tracking-inventory-item__badge">
                          <FontAwesomeIcon icon={faGlobe} /> Global
                        </span>
                      </div>
                      <div className="tracking-inventory-item__route">
                        <FontAwesomeIcon icon={faMapMarkerAlt} />{" "}
                        {ship.origin?.city || "Origin"} →{" "}
                        {ship.destination?.city || "Freetown"}
                      </div>
                    </div>
                    <div className="tracking-inventory-item__status">
                      <span
                        className="tracking-inventory-item__status-badge"
                        style={{ background: getStatusColor(ship.status) }}
                      >
                        <FontAwesomeIcon icon={getStatusIcon(ship.status)} />
                        {getStatusLabel(ship.status)}
                      </span>
                      <span className="tracking-inventory-item__date">
                        {ship.estimatedDelivery
                          ? `Est: ${new Date(ship.estimatedDelivery).toLocaleDateString()}`
                          : "TBD"}
                      </span>
                    </div>
                  </div>
                ))}
                <div className="tracking-inventory-list__note">
                  <FontAwesomeIcon icon={faInfoCircle} />
                  <span>
                    Pre-order customers will receive priority access when
                    shipments are cleared and ready for pickup. Contact us on
                    WhatsApp for updates.
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Tracking Tabs */}
          <div className="tracking-tabs-wrapper">
            {user && (
              <div className="tracking-tabs">
                <button
                  className={`tracking-tab ${activeTab === "track" ? "active" : ""}`}
                  onClick={() => setActiveTab("track")}
                >
                  <FontAwesomeIcon icon={faSearch} /> Track by Number
                </button>
                <button
                  className={`tracking-tab ${activeTab === "my-shipments" ? "active" : ""}`}
                  onClick={() => setActiveTab("my-shipments")}
                >
                  <FontAwesomeIcon icon={faBoxes} /> My Shipments
                </button>
              </div>
            )}

            {/* Track by Number */}
            {activeTab === "track" && (
              <>
                <form onSubmit={handleTrack} className="tracking-form">
                  <div className="tracking-input-group">
                    <div className="tracking-input-wrapper">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className="tracking-input-icon"
                      />
                      <input
                        type="text"
                        placeholder="Enter Tracking Number"
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                        className="tracking-input"
                      />
                    </div>
                    <button
                      type="submit"
                      className="tracking-submit-btn"
                      disabled={isSearching}
                    >
                      {isSearching ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} spin /> Tracking...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faSearch} /> Track Shipment
                        </>
                      )}
                    </button>
                  </div>
                </form>

                {error && (
                  <motion.div
                    className="tracking-error"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <span>{error}</span>
                  </motion.div>
                )}
              </>
            )}

            {/* My Shipments */}
            {activeTab === "my-shipments" && (
              <div className="my-shipments-section">
                <h3>
                  <FontAwesomeIcon icon={faBoxes} /> Your Active Shipments
                </h3>
                {myShipments.length === 0 ? (
                  <div className="my-shipments-empty">
                    <div className="my-shipments-empty__icon">
                      <FontAwesomeIcon icon={faBox} />
                    </div>
                    <p>You don't have any active shipments.</p>
                    <p className="my-shipments-empty__sub">
                      When you place an order with Kenali Enterprise, you can
                      track it here.
                    </p>
                    <div className="my-shipments-empty__actions">
                      <a href="/products" className="btn btn-primary">
                        Start Shopping
                      </a>
                      <a
                        href="https://wa.me/23231030808"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="whatsapp-support"
                      >
                        <FontAwesomeIcon icon={faWhatsappBrand} /> Contact
                        Support
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="my-shipments-list">
                    {myShipments.map((ship) => (
                      <div
                        key={ship._id}
                        className="my-shipment-item"
                        onClick={() => handleViewShipment(ship)}
                      >
                        <div>
                          <div className="my-shipment-item__tracking">
                            {ship.trackingNumber}
                            {ship.orderNumber && (
                              <span className="my-shipment-item__order">
                                Order: {ship.orderNumber}
                              </span>
                            )}
                          </div>
                          <div className="my-shipment-item__location">
                            <FontAwesomeIcon icon={faMapMarkerAlt} />
                            {ship.currentLocation || "Processing"}
                            {ship.origin?.country && (
                              <span>
                                <FontAwesomeIcon icon={faGlobe} />{" "}
                                {ship.origin.country}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="my-shipment-item__right">
                          <span
                            className="my-shipment-item__status"
                            style={{ background: getStatusColor(ship.status) }}
                          >
                            <FontAwesomeIcon icon={getStatusIcon(ship.status)} />
                            {getStatusLabel(ship.status)}
                          </span>
                          <span className="my-shipment-item__track">
                            <FontAwesomeIcon icon={faArrowRight} /> Track
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Shipment Details */}
            {loading && !shipment && <Loader />}

            {shipment && (
              <motion.div
                className="shipment-details"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {/* Header */}
                <div className="shipment-details__header">
                  <div>
                    <div className="shipment-details__tracking-label">
                      Tracking Number
                    </div>
                    <div className="shipment-details__tracking-number">
                      {shipment.trackingNumber}
                      {shipment.origin?.country && (
                        <span className="shipment-details__origin-badge">
                          <FontAwesomeIcon icon={faGlobe} /> Global
                        </span>
                      )}
                    </div>
                    {shipment.supplier?.name && (
                      <div className="shipment-details__supplier">
                        <FontAwesomeIcon icon={faBuilding} /> Supplier:{" "}
                        {shipment.supplier.name}
                      </div>
                    )}
                  </div>
                  <div
                    className="shipment-details__status-badge"
                    style={{ background: getStatusColor(shipment.status) }}
                  >
                    <FontAwesomeIcon icon={getStatusIcon(shipment.status)} />
                    {getStatusLabel(shipment.status)}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="shipment-details__progress">
                  <div className="shipment-details__progress-bar">
                    <div
                      className="shipment-details__progress-fill"
                      style={{
                        width: `${getProgressPercentage(shipment.status)}%`,
                      }}
                    />
                  </div>
                  <div className="shipment-details__progress-labels">
                    <span>Ordered</span>
                    <span>Processing</span>
                    <span>Shipped</span>
                    <span>In Transit</span>
                    <span>Customs</span>
                    <span>Delivered</span>
                  </div>
                </div>

                {/* Info Grid */}
                <div className="shipment-details__grid">
                  <div className="shipment-details__info">
                    <div className="shipment-details__info-label">
                      Current Location
                    </div>
                    <div className="shipment-details__info-value">
                      {shipment.currentLocation || "Processing"}
                    </div>
                  </div>
                  <div className="shipment-details__info">
                    <div className="shipment-details__info-label">
                      Estimated Delivery
                    </div>
                    <div className="shipment-details__info-value">
                      {shipment.estimatedDelivery
                        ? new Date(
                            shipment.estimatedDelivery,
                          ).toLocaleDateString()
                        : "To be confirmed"}
                    </div>
                  </div>
                  <div className="shipment-details__info">
                    <div className="shipment-details__info-label">Origin</div>
                    <div className="shipment-details__info-value">
                      {shipment.origin?.city || "Global Origin"},{" "}
                      {shipment.origin?.country || "Trusted Partners"}
                    </div>
                  </div>
                  <div className="shipment-details__info">
                    <div className="shipment-details__info-label">
                      Destination
                    </div>
                    <div className="shipment-details__info-value">
                      {shipment.destination?.city || "Freetown"},{" "}
                      {shipment.destination?.country || "Sierra Leone"}
                      <span className="shipment-details__flag">🇸🇱</span>
                    </div>
                  </div>
                </div>

                {/* Supplier Info */}
                <div className="shipment-details__supplier-info">
                  <FontAwesomeIcon icon={faInfoCircle} />
                  <span>
                    This shipment is from a trusted global partner.
                    {shipment.bulkDetails?.portOfLoading && (
                      <span>
                        {" "}
                        Loaded at {shipment.bulkDetails.portOfLoading}.
                      </span>
                    )}
                    {shipment.bulkDetails?.portOfDischarge && (
                      <span>
                        {" "}
                        Discharging at {shipment.bulkDetails.portOfDischarge}.
                      </span>
                    )}
                  </span>
                </div>

                {/* Timeline */}
                <div className="shipment-details__timeline">
                  <h4>
                    <FontAwesomeIcon icon={faClock} /> Shipment Timeline
                  </h4>
                  <div className="shipment-details__timeline-track">
                    <div className="shipment-details__timeline-line" />
                    {shipment.timeline?.map((event, idx) => (
                      <div
                        key={idx}
                        className="shipment-details__timeline-event"
                      >
                        <div
                          className="shipment-details__timeline-dot"
                          style={{
                            background:
                              idx === shipment.timeline.length - 1
                                ? "#D9A321"
                                : getStatusColor(event.status),
                          }}
                        />
                        <div>
                          <div className="shipment-details__timeline-date">
                            <FontAwesomeIcon icon={faClock} />{" "}
                            {new Date(event.timestamp).toLocaleString()}
                          </div>
                          <div className="shipment-details__timeline-status">
                            <FontAwesomeIcon
                              icon={getStatusIcon(event.status)}
                            />
                            {getStatusLabel(event.status)}
                          </div>
                          {event.location && (
                            <div className="shipment-details__timeline-location">
                              <FontAwesomeIcon icon={faMapMarkerAlt} />{" "}
                              {event.location}
                            </div>
                          )}
                          {event.description && (
                            <div className="shipment-details__timeline-description">
                              {event.description}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Help Section */}
                <div className="shipment-details__help">
                  <h4>
                    <FontAwesomeIcon icon={faInfoCircle} /> Need Help?
                  </h4>
                  <p>
                    If you have any questions about your shipment, please
                    contact our support team:
                  </p>
                  <div className="shipment-details__help-links">
                    <a href="mailto:kenalienterprisesl@gmail.com">
                      <FontAwesomeIcon icon={faEnvelope} />{" "}
                      kenalienterprisesl@gmail.com
                    </a>
                    <a href="tel:+23231030808">
                      <FontAwesomeIcon icon={faPhone} /> +232 31 030808
                    </a>
                    <a
                      href="https://wa.me/23231030808"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FontAwesomeIcon icon={faWhatsappBrand} /> WhatsApp
                    </a>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default OrderTracking;